import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Region, Locality } from '@shared/schema';

import { fetchJson } from '@/lib/queryClient';
import { useGeographyContext } from '@/hooks/useGeographyContext';

export function useRegionScope(regionSlug?: string, localitySlug?: string) {
  const { setGeography } = useGeographyContext();

  const {
    data: region,
    isLoading: regionLoading,
    isError: regionError,
  } = useQuery<Region>({
    queryKey: ['region', regionSlug],
    queryFn: () => fetchJson<Region>(`/api/regions/${regionSlug}`),
    enabled: !!regionSlug,
  });

  const {
    data: localityData,
    isLoading: localityLoading,
    isError: localityError,
  } = useQuery<{ region: Region; locality: Locality }>({
    queryKey: ['locality', regionSlug, localitySlug],
    queryFn: () => fetchJson<{ region: Region; locality: Locality }>(
      `/api/regions/${regionSlug}/${localitySlug}`
    ),
    enabled: !!regionSlug && !!localitySlug,
  });

  useEffect(() => {
    if (regionSlug) {
      setGeography(regionSlug, localitySlug);
    }
  }, [localitySlug, regionSlug, setGeography]);

  return {
    region: region ?? null,
    locality: localityData?.locality ?? null,
    isLoading: regionLoading || (!!localitySlug && localityLoading),
    isError: regionError || localityError,
  };
}
